import { CircleAlert, CircleX, FileText, Image, Video, Volume2 } from 'lucide-react'
import { ReactNode, useEffect, useMemo, type FC } from 'react'
import { webchatClasses } from '../../../../styles'
import type { FileType } from '../../../../types'
import { parseFileNameWithTimestamp } from '../../../../utils'

type FileAttachmentProps = {
  name: string
  type: FileType
  instance: File
  status: string
  removeFile: (name: string) => void
}

export const FileAttachment: FC<FileAttachmentProps> = ({ name, type, instance, status, removeFile }) => {
  const { loader, fileIcon, removeIcon, errorIcon, previewImage } = webchatClasses.composer.fileWidget

  const isImage = ['PNG', 'JPEG', 'JPG'].includes(getExtension(name))
  const previewUrl = useMemo(() => {
    if (!instance) {
      return ''
    }
    // Type guard: check if instance is actually a File or Blob (may not be after sessionStorage rehydration)
    const instanceAny = instance as unknown
    const isValidFile = instanceAny instanceof File || instanceAny instanceof Blob
    if (!isValidFile) {
      return ''
    }
    return isImage ? URL.createObjectURL(instanceAny as File) : ''
  }, [instance, isImage])

  // Cleanup object URL to prevent memory leaks
  useEffect(() => {
    if (previewUrl) {
      return () => {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  if (status === 'loading') {
    return (
      <FileCard name={name} status={status} removeButton={<div {...loader} />} extension={getExtension(name)}>
        <div {...fileIcon.container}>
          <FileIcon type={type} {...fileIcon.icon} />
        </div>
      </FileCard>
    )
  }

  if (status === 'error') {
    return (
      <FileCard
        name={name}
        status={status}
        removeButton={<RemoveButton removeFile={() => removeFile(name)} />}
        extension={'Upload Error'}
      >
        <div {...fileIcon.container}>
          <CircleAlert {...errorIcon} />
          <FileIcon type={type} {...fileIcon.icon} />
        </div>
      </FileCard>
    )
  }

  if (status == 'uploaded' && isImage) {
    return (
      <FileCard
        name={name}
        status={status}
        removeButton={<RemoveButton removeFile={() => removeFile(name)} />}
        extension={getExtension(name)}
      >
        <div {...fileIcon.container}>
          <img src={previewUrl} alt={name} {...previewImage} />
        </div>
      </FileCard>
    )
  }

  return (
    <FileCard
      name={name}
      status={status}
      removeButton={<CircleX onClick={() => removeFile(name)} {...removeIcon} />}
      extension={getExtension(name)}
    >
      <div {...fileIcon.container}>
        <FileIcon type={type} {...fileIcon.icon} />
      </div>
    </FileCard>
  )
}

type FileCardProps = {
  name: string
  status: string
  removeButton: ReactNode
  children: ReactNode
  extension: string
}

const FileCard: FC<FileCardProps> = ({ name, status, removeButton, children, extension }) => {
  const { container, metadata } = webchatClasses.composer.fileWidget
  const displayName = parseFileNameWithTimestamp(name)
  return (
    <div data-status={status} title={name} {...container}>
      {removeButton}
      {children}
      <div {...metadata.container}>
        <div {...metadata.name}>{displayName}</div>
        <div {...metadata.type}>{extension}</div>
      </div>
    </div>
  )
}

const FileIcon: FC<{ type: string; className: string }> = ({ type, ...props }) => {
  if (type === 'image') return <Image {...props} />
  if (type === 'video') return <Video {...props} />
  if (type === 'audio') return <Volume2 {...props} />
  return <FileText {...props} />
}

const getExtension = (name: string) => {
  const extension = name.split('.').pop()
  return extension ? extension.toUpperCase() : 'File'
}

const RemoveButton: FC<{ removeFile: () => void }> = ({ removeFile }) => {
  const {
    composer: {
      fileWidget: { removeIcon },
    },
  } = webchatClasses

  return (
    <CircleX role="button" tabIndex={0} aria-label="Remove File Button" onClick={() => removeFile()} {...removeIcon} />
  )
}
